/**
 * Pricing Hooks
 * 
 * React hooks for subscriptions, usage tracking, and AI credits.
 * 
 * @module usePricing
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getPricingTiers,
  getPricingModules,
  getQuote as getQuoteApi,
  compareTiers as compareTiersApi,
  getSubscription,
  upgradeSubscription as upgradeApi,
  previewUpgrade,
  enableModule as enableModuleApi,
  disableModule as disableModuleApi,
  getModules,
  getUsageSummary,
  getQuota,
  getUsageAlerts,
  getBillingSummary,
  getCreditBalance,
  getCreditLedger,
  getCreditsUsageSummary,
  getRateCard,
  calculateCredits as calculateCreditsApi,
  trackUsage as trackUsageApi,
  PricingApiError,
} from '@/services/pricingApi';
import { syncBillingFromStripe } from '@/services/paymentApi';
import type {
  TierType,
  PricingTier,
  PricingModule,
  PrepaidPack,
  QuoteRequest,
  QuoteResponse,
  CompareRequest,
  CompareResponse,
  Subscription,
  UpgradePreviewResponse,
  ModuleInfo,
  UsageSummaryResponse,
  QuotaResponse,
  QuotaInfo,
  UsageAlert,
  BillingSummaryResponse,
  CreditBalance,
  LedgerEntry,
  LedgerRequest,
  CreditsUsageSummaryResponse,
  RateCardItem,
  CalculateCreditsRequest,
  CalculateCreditsResponse,
  TrackUsageRequest,
} from '@/types/pricing';

// ============================================
// usePricingTiers Hook
// ============================================

interface UsePricingTiersReturn {
  tiers: PricingTier[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getTier: (tierType: TierType) => PricingTier | undefined;
}

/**
 * Hook for pricing tiers (public)
 */
export function usePricingTiers(): UsePricingTiersReturn {
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTiers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getPricingTiers();
      setTiers(response.tiers || []);
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getTier = useCallback(
    (tierType: TierType) => tiers.find(t => t.tier_type === tierType),
    [tiers]
  );

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  return {
    tiers,
    isLoading,
    error,
    refetch: fetchTiers,
    getTier,
  };
}

// ============================================
// usePricingModules Hook (public - no auth needed)
// ============================================

interface UsePricingModulesReturn {
  modules: PricingModule[];
  prepaidPacks: Record<string, PrepaidPack[]>;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getModule: (moduleId: string) => PricingModule | undefined;
  getModulePriceForTier: (moduleId: string, tier: TierType) => number | null;
  isModuleAvailableForTier: (moduleId: string, tier: TierType) => boolean;
}

/**
 * Hook for available add-on modules (public)
 */
export function usePricingModules(): UsePricingModulesReturn {
  const [modules, setModules] = useState<PricingModule[]>([]);
  const [prepaidPacks, setPrepaidPacks] = useState<Record<string, PrepaidPack[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchModules = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getPricingModules();
      setModules(response.modules || []);
      setPrepaidPacks(response.prepaid_packs || {});
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      } else {
        setError('Failed to fetch modules');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getModule = useCallback(
    (moduleId: string) => modules.find(m => m.module_id === moduleId),
    [modules]
  );

  const getModulePriceForTier = useCallback(
    (moduleId: string, tier: TierType): number | null => {
      const mod = modules.find(m => m.module_id === moduleId);
      if (!mod) return null;
      switch (tier) {
        case 'launch': return null; // Not available on launch
        case 'growth': return mod.growth_monthly_fee;
        case 'scale': return mod.scale_monthly_fee;
        case 'enterprise': return mod.enterprise_monthly_fee;
        default: return null;
      }
    },
    [modules]
  );

  const isModuleAvailableForTier = useCallback(
    (moduleId: string, tier: TierType): boolean => {
      const mod = modules.find(m => m.module_id === moduleId);
      if (!mod) return false;
      switch (tier) {
        case 'launch': return mod.available_on_launch;
        case 'growth': return mod.available_on_growth;
        case 'scale': return mod.available_on_scale;
        case 'enterprise': return mod.available_on_enterprise;
        default: return false;
      }
    },
    [modules]
  );

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  return {
    modules,
    prepaidPacks,
    isLoading,
    error,
    refetch: fetchModules,
    getModule,
    getModulePriceForTier,
    isModuleAvailableForTier,
  };
}

// ============================================
// usePricingQuote Hook
// ============================================

interface UsePricingQuoteReturn {
  quote: QuoteResponse | null;
  isLoading: boolean;
  error: string | null;
  getQuote: (data: QuoteRequest) => Promise<QuoteResponse | null>;
  compareTiers: (data: CompareRequest) => Promise<CompareResponse | null>;
}

/**
 * Hook for pricing quotes (public)
 */
export function usePricingQuote(): UsePricingQuoteReturn {
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getQuote = useCallback(async (data: QuoteRequest) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getQuoteApi(data);
      setQuote(response);
      return response;
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const compareTiers = useCallback(async (data: CompareRequest) => {
    try {
      setIsLoading(true);
      setError(null);
      return await compareTiersApi(data);
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    quote,
    isLoading,
    error,
    getQuote,
    compareTiers,
  };
}

// ============================================
// useSubscription Hook
// ============================================

interface UseSubscriptionReturn {
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  upgrade: (newTier: TierType, prorate?: boolean) => Promise<boolean>;
  previewUpgrade: (newTier: TierType) => Promise<UpgradePreviewResponse | null>;
  enableModule: (moduleName: 'signalstack' | 'blyn' | 'ai_decisioning' | 'web_identity') => Promise<boolean>;
  disableModule: (moduleName: 'signalstack' | 'blyn' | 'ai_decisioning' | 'web_identity') => Promise<boolean>;
}

function normalizeExpiredSubscription(subscription: Subscription | null | undefined): Subscription | null {
  if (!subscription) return null;

  const accessEnd = subscription.subscription_ends_at || subscription.current_period_end;
  if (!accessEnd) return subscription;

  const accessEndTime = new Date(accessEnd).getTime();
  const hasAccessEnded = Number.isFinite(accessEndTime) && accessEndTime <= Date.now();
  const hasExplicitEndDate = Boolean(subscription.subscription_ends_at);
  const isPendingCancellation = subscription.cancellation_scheduled_at_period_end === true;

  if (!hasAccessEnded || (!isPendingCancellation && !hasExplicitEndDate)) return subscription;

  return {
    ...subscription,
    status: 'cancelled',
    is_trial: false,
    cancellation_scheduled_at_period_end: false,
    next_billing_date: null,
  };
}

/**
 * Hook for subscription management
 */
export function useSubscription(userID: number | null): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVersionRef = useRef(0);

  // `showFirstResult`: render the first DB result right away and reconcile with Stripe in the
  // background (initial page load). Explicit refetches (after payment/upgrade) keep waiting for
  // the synced result so callers that `await refetch()` still see fresh data.
  const loadSubscription = useCallback(async (showFirstResult: boolean) => {
    if (!userID) {
      setIsLoading(false);
      return;
    }
    const version = ++fetchVersionRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const initialResponse = await getSubscription(userID);
      if (version !== fetchVersionRef.current) return;
      const initialSubscription = normalizeExpiredSubscription(initialResponse.subscription);
      if (initialSubscription?.status === 'cancelled') {
        setSubscription(initialSubscription);
        return;
      }

      if (showFirstResult) {
        setSubscription(initialSubscription);
        setIsLoading(false);
      }

      try {
        await syncBillingFromStripe({ userID });
      } catch {
        /* Still load subscription from DB if payment sync fails */
      }
      let response;
      try {
        response = await getSubscription(userID);
      } catch (err) {
        // Background reconcile failed: keep what we already showed.
        if (showFirstResult) return;
        throw err;
      }
      if (version !== fetchVersionRef.current) return;
      setSubscription(normalizeExpiredSubscription(response.subscription));
    } catch (err) {
      if (version !== fetchVersionRef.current) return;
      if (err instanceof PricingApiError) {
        setError(err.message);
      } else {
        setError('Failed to fetch subscription');
      }
      setSubscription(null);
    } finally {
      if (version === fetchVersionRef.current) setIsLoading(false);
    }
  }, [userID]);

  const fetchSubscription = useCallback(() => loadSubscription(false), [loadSubscription]);

  const upgrade = useCallback(async (newTier: TierType, prorate = true) => {
    if (!userID) return false;
    try {
      setIsLoading(true);
      setError(null);
      const response = await upgradeApi({ userID, new_tier: newTier, prorate });
      if (response.success) {
        setSubscription(normalizeExpiredSubscription(response.subscription));
        return true;
      }
      return false;
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userID]);

  const previewUpgradeFn = useCallback(async (newTier: TierType) => {
    if (!userID) return null;
    try {
      setError(null);
      return await previewUpgrade({ userID, new_tier: newTier, prorate: true });
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      }
      return null;
    }
  }, [userID]);

  const enableModule = useCallback(async (
    moduleName: 'signalstack' | 'blyn' | 'ai_decisioning' | 'web_identity'
  ) => {
    if (!userID) return false;
    try {
      setIsLoading(true);
      setError(null);
      const response = await enableModuleApi({ userID, module_name: moduleName });
      if (response.success) {
        setSubscription(normalizeExpiredSubscription(response.subscription));
        return true;
      }
      return false;
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userID]);

  const disableModule = useCallback(async (
    moduleName: 'signalstack' | 'blyn' | 'ai_decisioning' | 'web_identity'
  ) => {
    if (!userID) return false;
    try {
      setIsLoading(true);
      setError(null);
      const response = await disableModuleApi({ userID, module_name: moduleName });
      if (response.success) {
        setSubscription(normalizeExpiredSubscription(response.subscription));
        return true;
      }
      return false;
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userID]);

  useEffect(() => {
    if (userID) {
      loadSubscription(true);
    } else {
      setIsLoading(false);
    }
  }, [userID, loadSubscription]);

  return {
    subscription,
    isLoading,
    error,
    refetch: fetchSubscription,
    upgrade,
    previewUpgrade: previewUpgradeFn,
    enableModule,
    disableModule,
  };
}

// ============================================
// useModules Hook
// ============================================

interface UseModulesReturn {
  modules: ModuleInfo[];
  totalMonthlyFees: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isModuleEnabled: (moduleName: string) => boolean;
}

/**
 * Hook for enabled modules
 */
export function useModules(userID: number | null): UseModulesReturn {
  const [modules, setModules] = useState<ModuleInfo[]>([]);
  const [totalMonthlyFees, setTotalMonthlyFees] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchModules = useCallback(async () => {
    if (!userID) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await getModules(userID);
      // Backend often returns catalog id on `module_id` only; UI keys off `module_id` from pricing catalog.
      // Normalize so `module_name` matches for legacy checks (e.g. Add-ons "enabled" state).
      const raw = response.modules || [];
      setModules(
        raw.map((m) => ({
          ...m,
          module_name: m.module_name ?? m.module_id,
        }))
      );
      setTotalMonthlyFees(response.total_monthly_fees || 0);
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      } else {
        setError('Failed to fetch modules');
      }
      // Set empty modules on error so UI doesn't stay stuck
      setModules([]);
    } finally {
      setIsLoading(false);
    }
  }, [userID]);

  const isModuleEnabled = useCallback(
    (moduleName: string) =>
      modules.some(
        (m) => m.is_enabled && (m.module_id === moduleName || m.module_name === moduleName)
      ),
    [modules]
  );

  useEffect(() => {
    if (userID) {
      fetchModules();
    } else {
      setIsLoading(false);
    }
  }, [userID, fetchModules]);

  return {
    modules,
    totalMonthlyFees,
    isLoading,
    error,
    refetch: fetchModules,
    isModuleEnabled,
  };
}

// ============================================
// useUsage Hook
// ============================================

interface UsageHistoryItem {
  period: string;
  usage_quantity: number;
  overage_charge: number;
}

interface UseUsageReturn {
  summary: UsageSummaryResponse | null;
  quotas: Record<string, QuotaInfo>;
  alerts: UsageAlert[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  trackUsage: (metricName: string, quantity?: number, source?: string) => Promise<boolean>;
  getUsageHistory: (metricName: string, months?: number) => Promise<UsageHistoryItem[]>;
}

/**
 * Hook for usage tracking
 */
export function useUsage(userID: number | null, billingPeriod?: string): UseUsageReturn {
  const [summary, setSummary] = useState<UsageSummaryResponse | null>(null);
  const [quotas, setQuotas] = useState<Record<string, QuotaInfo>>({});
  const [alerts, setAlerts] = useState<UsageAlert[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = useCallback(async () => {
    if (!userID) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      
      const [summaryRes, quotaRes, alertsRes] = await Promise.all([
        getUsageSummary({ userID, billing_period: billingPeriod }),
        getQuota({ userID }),
        getUsageAlerts({ userID, include_acknowledged: false }),
      ]);
      
      setSummary(summaryRes);
      setQuotas(quotaRes.quotas || {});
      setAlerts(alertsRes.alerts || []);
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      } else {
        setError('Failed to fetch usage data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [userID, billingPeriod]);

  const trackUsage = useCallback(async (
    metricName: string,
    quantity = 1,
    source?: string
  ) => {
    if (!userID) return false;
    try {
      await trackUsageApi({ userID, metric_name: metricName, quantity, source });
      return true;
    } catch (err) {
      return false;
    }
  }, [userID]);

  const getUsageHistory = useCallback(async (
    metricName: string,
    months = 6
  ): Promise<UsageHistoryItem[]> => {
    if (!userID) return [];
    try {
      const { getUsageHistory: getUsageHistoryApi } = await import('@/services/pricingApi');
      const response = await getUsageHistoryApi({
        userID,
        metric_name: metricName,
        months,
      });
      return response.history || [];
    } catch (err) {
      console.error('Failed to fetch usage history:', err);
      return [];
    }
  }, [userID]);

  useEffect(() => {
    if (userID) {
      fetchUsage();
    } else {
      setIsLoading(false);
    }
  }, [userID, fetchUsage]);

  return {
    summary,
    quotas,
    alerts,
    isLoading,
    error,
    refetch: fetchUsage,
    trackUsage,
    getUsageHistory,
  };
}

// ============================================
// useBilling Hook
// ============================================

interface UseBillingReturn {
  billing: BillingSummaryResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for billing summary
 */
export function useBilling(
  userID: number | null,
  billingPeriod?: string,
  previewOnly = true
): UseBillingReturn {
  const [billing, setBilling] = useState<BillingSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBilling = useCallback(async () => {
    if (!userID) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await getBillingSummary({
        userID,
        billing_period: billingPeriod,
        preview_only: previewOnly,
      });
      setBilling(response);
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      } else {
        setError('Failed to fetch billing summary');
      }
    } finally {
      setIsLoading(false);
    }
  }, [userID, billingPeriod, previewOnly]);

  useEffect(() => {
    if (userID) {
      fetchBilling();
    } else {
      setIsLoading(false);
    }
  }, [userID, fetchBilling]);

  return {
    billing,
    isLoading,
    error,
    refetch: fetchBilling,
  };
}

// ============================================
// useCredits Hook
// ============================================

interface UseCreditsReturn {
  balance: CreditBalance | null;
  ledger: LedgerEntry[];
  usageSummary: CreditsUsageSummaryResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  fetchLedger: (options?: Partial<LedgerRequest>) => Promise<void>;
  calculateCredits: (data: CalculateCreditsRequest) => Promise<CalculateCreditsResponse | null>;
}

/**
 * Hook for AI credits
 */
export function useCredits(userID: number | null, billingPeriod?: string): UseCreditsReturn {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [usageSummary, setUsageSummary] = useState<CreditsUsageSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = useCallback(async () => {
    if (!userID) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      
      const [balanceRes, summaryRes] = await Promise.all([
        getCreditBalance(userID),
        getCreditsUsageSummary({ userID, billing_period: billingPeriod }),
      ]);
      
      setBalance(balanceRes);
      setUsageSummary(summaryRes);
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      } else {
        setError('Failed to fetch credits');
      }
    } finally {
      setIsLoading(false);
    }
  }, [userID, billingPeriod]);

  const fetchLedger = useCallback(async (options?: Partial<LedgerRequest>) => {
    if (!userID) return;
    try {
      const response = await getCreditLedger({
        userID,
        limit: 50,
        page: 1,
        ...options,
      });
      setLedger(response.entries || []);
    } catch (err) {
      console.error('Failed to fetch ledger:', err);
    }
  }, [userID]);

  const calculateCredits = useCallback(async (data: CalculateCreditsRequest) => {
    try {
      return await calculateCreditsApi(data);
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    if (userID) {
      fetchBalance();
    } else {
      setIsLoading(false);
    }
  }, [userID, fetchBalance]);

  return {
    balance,
    ledger,
    usageSummary,
    isLoading,
    error,
    refetch: fetchBalance,
    fetchLedger,
    calculateCredits,
  };
}

// ============================================
// useRateCard Hook
// ============================================

interface UseRateCardReturn {
  rateCard: RateCardItem[];
  creditPolicyVersion: string;
  creditValueAllowance: number;
  includedCredits: number;
  overagePerK: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for rate card
 */
export function useRateCard(subscriptionTier?: TierType): UseRateCardReturn {
  const [rateCard, setRateCard] = useState<RateCardItem[]>([]);
  const [creditPolicyVersion, setCreditPolicyVersion] = useState('');
  const [creditValueAllowance, setCreditValueAllowance] = useState(0);
  const [includedCredits, setIncludedCredits] = useState(0);
  const [overagePerK, setOveragePerK] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRateCard = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getRateCard(
        subscriptionTier ? { subscription_tier: subscriptionTier } : undefined
      );
      setRateCard(response.rate_card || []);
      setCreditPolicyVersion(response.credit_policy_version || '');
      setCreditValueAllowance(response.credit_value_allowance || 0);
      setIncludedCredits(response.included_credits || 0);
      setOveragePerK(response.overage_per_1k || 0);
    } catch (err) {
      if (err instanceof PricingApiError) {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [subscriptionTier]);

  useEffect(() => {
    fetchRateCard();
  }, [fetchRateCard]);

  return {
    rateCard,
    creditPolicyVersion,
    creditValueAllowance,
    includedCredits,
    overagePerK,
    isLoading,
    error,
    refetch: fetchRateCard,
  };
}

const usePricingExports = {
  usePricingTiers,
  usePricingQuote,
  useSubscription,
  useModules,
  useUsage,
  useBilling,
  useCredits,
  useRateCard,
};
export default usePricingExports;

